// @tennis-matches/services/api/client
// Zentraler Fetch-Client: einheitliche Response-Interpretation, credentials, optional Bearer.

import { getSession } from "../auth/auth.service.js";

/**
 * Erstellt einen API-Client mit einheitlichem Request/Response-Verhalten.
 * @param {{ baseUrl?: string }} [opts] - baseUrl optional (fehlt = relative URLs)
 * @returns {{ get: (path, opts?) => Promise<...>, post: (path, body, opts?) => Promise<...> }}
 */
export function createApiClient(opts = {}) {
  const baseUrl = typeof opts.baseUrl === "string" ? opts.baseUrl.replace(/\/+$/, "") : "";

  function resolveUrl(path) {
    const p = typeof path === "string" ? path : "";
    const normalized = p.startsWith("/") ? p : `/${p}`;
    return baseUrl ? `${baseUrl}${normalized}` : normalized;
  }

  /**
   * Liest bei auth === true den Access Token aus der Supabase-Session und setzt Authorization.
   * @param {boolean} auth
   * @returns {Promise<Record<string, string>>}
   */
  async function getAuthHeaders(auth) {
    if (!auth) return {};
    try {
      const session = await getSession();
      const token = session?.access_token;
      if (token) return { Authorization: `Bearer ${token}` };
    } catch (_) {}
    return {};
  }

  /**
   * Parst Response zu einheitlichem Format.
   * data = json.data ?? json (damit Endpoints die { ok, flags } liefern, data.flags nutzbar ist).
   * Einheitliches Result: { ok, data, reason, status, detail }.
   */
  async function parseResponse(res) {
    const ct = String(res.headers.get("content-type") || "");
    let json = {};
    try {
      if (ct.includes("application/json")) {
        json = await res.json();
      } else {
        const text = await res.text();
        if (text) json = { reason: "non-json", detail: text.slice(0, 200) };
      }
    } catch (_) {
      json = { reason: "parse-error" };
    }
    if (json && typeof json !== "object") json = {};
    const ok = res.ok && json.ok !== false;
    const data = json.data !== undefined ? json.data : json;
    const reason = json.reason ?? json.message ?? undefined;
    const detail = json.detail ?? undefined;
    return { ok, status: res.status, data, reason, detail };
  }

  /**
   * GET request.
   * @param {string} path
   * @param {{ auth?: boolean }} [opts] - auth default true
   */
  async function get(path, opts = {}) {
    const auth = opts.auth !== false;
    const headers = { ...(opts.headers || {}), ...(await getAuthHeaders(auth)) };
    const res = await fetch(resolveUrl(path), {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: { "Content-Type": "application/json", ...headers },
    });
    return parseResponse(res);
  }

  /**
   * POST (oder method aus opts) request.
   * @param {string} path
   * @param {unknown} body
   * @param {{ auth?: boolean, method?: string, headers?: Record<string, string> }} [opts]
   */
  async function post(path, body, opts = {}) {
    const auth = opts.auth !== false;
    const method = (opts.method || "POST").toUpperCase();
    const headers = { ...(opts.headers || {}), ...(await getAuthHeaders(auth)) };
    const res = await fetch(resolveUrl(path), {
      method,
      credentials: "include",
      cache: "no-store",
      headers: { "Content-Type": "application/json", ...headers },
      body: body != null ? JSON.stringify(body) : undefined,
    });
    return parseResponse(res);
  }

  /**
   * PUT request.
   * @param {string} path
   * @param {unknown} body
   * @param {{ auth?: boolean, headers?: Record<string, string> }} [opts]
   */
  async function put(path, body, opts = {}) {
    return post(path, body, { ...opts, method: "PUT" });
  }

  return { get, post, put };
}

/** Singleton-Client ohne baseUrl (relative URLs). */
let defaultClient = null;

export function getApiClient() {
  if (!defaultClient) defaultClient = createApiClient({});
  return defaultClient;
}
